import type { Middleware } from 'openapi-fetch';

/**
 * Request header that opts out of the unauth dispatcher.
 *
 * Set on call sites where a 401 represents an in-flow failure (wrong password,
 * invalid token, expired magic link, etc.) rather than session expiry. The
 * backend ignores unknown headers, so no stripping is required.
 *
 * Grep `x-sentinel-skip-unauth` to enumerate every flow that bypasses the
 * dispatcher.
 */
export const SKIP_UNAUTH_HEADER = 'x-sentinel-skip-unauth';

/**
 * Slots into `createApiClient`'s middleware chain after CSRF + step-up. On
 * `response.status === 401`, dispatches a `sentinel:unauthenticated` window
 * event so listeners (SessionProvider state-clear + SPA redirect helpers) can
 * react. Returns `undefined` to leave the response unmodified — callers still
 * receive the 401 and render their own per-feature error UI.
 */
export function createUnauthMiddleware(): Middleware {
  return {
    onResponse({ request, response }) {
      if (response.status !== 401) return;
      if (request.headers.get(SKIP_UNAUTH_HEADER) === '1') return;
      if (typeof window === 'undefined') return;
      window.dispatchEvent(new CustomEvent('sentinel:unauthenticated'));
      return;
    },
  };
}
